/**
 * The platform panel's calls and the shapes they return (Kapitan staff only).
 *
 * Every call goes through `apiRequest`, so auth, refresh and error mapping are shared with
 * the rest of the app. The server enforces the platform-admin check; the route guard only
 * decides what to draw.
 */
import { apiRequest, type Paginated } from "@/lib/api";

export type TenantStatus = "active" | "suspended_billing" | "suspended_admin" | "closed";

export interface PlatformTenantRow {
  id: number;
  name: string;
  slug: string;
  status: TenantStatus;
  status_display: string;
  balance: string;
  members: number;
  /** The package's name, or null when the agency is on none. */
  tariff: string | null;
  /** The newest sign-in of an active member, or null when nobody has signed in. */
  last_activity_at: string | null;
  /** Channel accounts that cannot carry a message now. */
  channels_needing_attention: number;
  created_at: string;
}

export interface PlatformTenantSummary {
  id: number;
  name: string;
  slug: string;
  status: TenantStatus;
  status_display: string;
}

export interface TenantCreated {
  tenant: PlatformTenantSummary;
  owner_email: string;
  password_link: string;
  /** False when the owner was not e-mailed; the panel then shows the link to copy (D580). */
  invitation_sent: boolean;
}

export interface TenantCreateInput {
  name: string;
  slug: string;
  owner_email: string;
  owner_name: string;
  branches: string[];
  timezone: string;
  default_currency: "UZS" | "USD";
  interface_language: string;
  tariff: number | null;
}

export interface Tariff {
  id: number;
  name: string;
  monthly_price: string;
  max_members: number;
  monthly_ai_credits: number;
  extra_credit_price: string;
  is_active: boolean;
}

export type TariffInput = Omit<Tariff, "id">;

export type DemoStatus = "new" | "contacted" | "demo_scheduled" | "converted" | "rejected";

export interface DemoRequestRow {
  id: number;
  created_at: string;
  full_name: string;
  agency_name: string;
  phone: string;
  city: string;
  staff_count: number | null;
  staff_size: string;
  locale: string;
  main_channel: string;
  note: string;
  status: DemoStatus;
  status_display: string;
  tenant_id: number | null;
}

export interface DemoConverted {
  demo_request: DemoRequestRow;
  tenant_id: number;
  tenant_name: string;
  owner_email: string;
  password_link: string;
}

export type CreditRequestStatus = "pending" | "confirmed" | "rejected";

export interface CreditRequestRow {
  id: number;
  tenant_id: number;
  tenant_name: string;
  credits: number;
  amount: string;
  status: CreditRequestStatus;
  status_display: string;
  note: string;
  created_at: string;
  decided_at: string | null;
  decision_reason: string;
}

/** One agency's supplier account as the platform panel reads it (D212). No login, no password. */
/** One airport on the shared list. Coordinates are decimal strings, as the API sends them. */
export interface PlatformAirport {
  id: number;
  iata: string;
  name: string;
  country_code: string;
  latitude: string | null;
  longitude: string | null;
  is_international: boolean;
  is_active: boolean;
}

export interface PlatformAirportDraft {
  iata: string;
  name: string;
  country_code: string;
  latitude: string | null;
  longitude: string | null;
  is_international: boolean;
  is_active: boolean;
}

/** One shared destination (D519). Names are keyed by language; `uz_latn` is the shipped one. */
export interface PlatformDestination {
  id: number;
  kind: string;
  parent: number | null;
  country_code: string;
  names: Record<string, string>;
  aliases: string[];
  latitude: string | null;
  longitude: string | null;
  timezone: string;
  main_airports: string[];
  is_active: boolean;
}

export interface PlatformDestinationDraft {
  kind: string;
  country_code: string;
  names: Record<string, string>;
  aliases: string[];
  is_active: boolean;
}

/** A supplier the adapter registry knows, with what it declares. No credential is in it. */
export interface ProviderCatalogueRow {
  slug: string;
  name: string;
  platform: string;
  base_url: string;
  capabilities: string[];
  declares_search: boolean;
  notes: string;
}

/** A default agency message: its key and the placeholders it needs. The wording is not listed. */
export interface TemplateCatalogueRow {
  key: string;
  placeholders: string[];
  length: number;
}

/** One hotel on the shared catalogue, written by the matcher (§9.6). */
export interface HotelCatalogueRow {
  id: number;
  name: string;
  destination_name: string;
  country_code: string;
  stars: number | null;
  is_active: boolean;
}

export interface ProviderHealthRow {
  provider: string;
  provider_name: string;
  platform: string;
  tenant: string;
  last_checked_at: string | null;
  last_status: string;
  last_detail: string;
  last_latency_ms: number | null;
  consecutive_failures: number;
  /** Newest search run that answered, across all time; null when none ever did (D516). */
  last_successful_request_at: string | null;
  /** Share of the last day's asks that failed, one decimal; null when nothing was asked, never 0. */
  error_rate_percent_24h: number | null;
  /** The circuit breaker's state: closed, half_open or open (§9.5). */
  circuit_state: "closed" | "half_open" | "open";
  circuit_retry_at: string | null;
  adapter_version: string;
  responsible: string;
  is_healthy: boolean;
}

export interface AuditRow {
  id: number;
  at: string;
  tenant_name: string | null;
  actor_kind: string;
  actor_label: string;
  action: string;
  severity: string;
  object_type: string;
  object_id: string;
  reason: string;
  diff: Record<string, unknown>;
}

export interface SupportSessionRow {
  id: number;
  tenant: number;
  tenant_name: string;
  admin: number;
  admin_name: string;
  reason: string;
  expires_at: string;
  write_access: boolean;
  ended_at: string | null;
  ended_by: number | null;
  is_open: boolean;
  created_at: string;
}

export interface OpenSupportInput {
  reason: string;
  minutes: number;
  write_access: boolean;
}

export const platformApi = {
  tenant: (id: number): Promise<PlatformTenantRow> => apiRequest(`/api/platform/tenants/${id}/`),

  tenants: (page: number): Promise<Paginated<PlatformTenantRow>> =>
    apiRequest("/api/platform/tenants/", { query: { page } }),

  createTenant: (body: TenantCreateInput): Promise<TenantCreated> =>
    apiRequest("/api/platform/tenants/", { method: "POST", body }),

  changeTenantStatus: (
    tenantId: number,
    body: { status: "active" | "suspended_admin" | "closed"; reason: string },
  ): Promise<PlatformTenantSummary> =>
    apiRequest(`/api/platform/tenants/${tenantId}/status/`, { method: "POST", body }),

  assignTariff: (tenantId: number, tariffId: number): Promise<unknown> =>
    apiRequest(`/api/platform/tenants/${tenantId}/subscription/`, {
      method: "POST",
      body: { tariff: tariffId },
    }),

  tariffs: (page: number): Promise<Paginated<Tariff>> =>
    apiRequest("/api/platform/tariffs/", { query: { page } }),

  createTariff: (body: TariffInput): Promise<Tariff> =>
    apiRequest("/api/platform/tariffs/", { method: "POST", body }),

  updateTariff: (tariffId: number, body: Partial<TariffInput>): Promise<Tariff> =>
    apiRequest(`/api/platform/tariffs/${tariffId}/`, { method: "PATCH", body }),

  demoRequests: (page: number, status?: string): Promise<Paginated<DemoRequestRow>> =>
    apiRequest("/api/platform/demo-requests/", { query: { page, status: status || undefined } }),

  changeDemoStatus: (demoId: number, status: DemoStatus): Promise<DemoRequestRow> =>
    apiRequest(`/api/platform/demo-requests/${demoId}/status/`, { method: "POST", body: { status } }),

  convertDemo: (
    demoId: number,
    body: { owner_email: string; owner_name: string; branches: string[] },
  ): Promise<DemoConverted> =>
    apiRequest(`/api/platform/demo-requests/${demoId}/convert/`, { method: "POST", body }),

  creditRequests: (page: number, status?: string): Promise<Paginated<CreditRequestRow>> =>
    apiRequest("/api/platform/credit-requests/", { query: { page, status: status || undefined } }),

  confirmCredit: (requestId: number): Promise<CreditRequestRow> =>
    apiRequest(`/api/platform/credit-requests/${requestId}/confirm/`, { method: "POST" }),

  rejectCredit: (requestId: number, reason: string): Promise<CreditRequestRow> =>
    apiRequest(`/api/platform/credit-requests/${requestId}/reject/`, {
      method: "POST",
      body: { reason },
    }),

  audit: (page: number, tenant?: number): Promise<Paginated<AuditRow>> =>
    apiRequest("/api/platform/audit/", { query: { page, tenant } }),

  /** The shared airport list every agency's hotel distances are measured against (D390). */
  airports: (page: number): Promise<Paginated<PlatformAirport>> =>
    apiRequest("/api/platform/airports/", { query: { page } }),

  createAirport: (body: PlatformAirportDraft): Promise<PlatformAirport> =>
    apiRequest("/api/platform/airports/", { method: "POST", body }),

  /** Corrections and retirement (`is_active`). An airport is never deleted. */
  updateAirport: (id: number, patch: Partial<PlatformAirportDraft>): Promise<PlatformAirport> =>
    apiRequest(`/api/platform/airports/${id}/`, { method: "PATCH", body: patch }),

  /** The shared destination catalogue (D519). Retired with `is_active`, never deleted. */
  destinations: (page: number): Promise<Paginated<PlatformDestination>> =>
    apiRequest("/api/platform/destinations/", { query: { page } }),

  createDestination: (body: PlatformDestinationDraft): Promise<PlatformDestination> =>
    apiRequest("/api/platform/destinations/", { method: "POST", body }),

  updateDestination: (
    id: number,
    patch: Partial<PlatformDestinationDraft>,
  ): Promise<PlatformDestination> =>
    apiRequest(`/api/platform/destinations/${id}/`, { method: "PATCH", body: patch }),

  /** Read-only lists beside the destinations and airports (R0d69a3). */
  providerCatalogue: (): Promise<{ count: number; results: ProviderCatalogueRow[] }> =>
    apiRequest("/api/platform/catalogues/providers/"),

  templateCatalogue: (): Promise<{ count: number; results: TemplateCatalogueRow[] }> =>
    apiRequest("/api/platform/catalogues/templates/"),

  hotelCatalogue: (page: number): Promise<Paginated<HotelCatalogueRow>> =>
    apiRequest("/api/platform/catalogues/hotels/", { query: { page } }),

  /** Every agency's supplier accounts, with the canary's last word on each (D212). */
  providerHealth: (): Promise<{ results: ProviderHealthRow[] }> =>
    apiRequest("/api/platform/providers/health/"),

  supportSessions: (page: number): Promise<Paginated<SupportSessionRow>> =>
    apiRequest("/api/platform/support-sessions/", { query: { page } }),

  openSupport: (tenantId: number, body: OpenSupportInput): Promise<SupportSessionRow> =>
    apiRequest(`/api/platform/tenants/${tenantId}/support/`, { method: "POST", body }),

  closeSupport: (sessionId: number): Promise<SupportSessionRow> =>
    apiRequest(`/api/platform/support-sessions/${sessionId}/close/`, { method: "POST" }),
};

/** The agency's banner for an open support session (TZ §2.3). */
export interface SupportBanner {
  active: boolean;
  session: {
    id: number;
    reason: string;
    expires_at: string;
    write_access: boolean;
  } | null;
}

export const supportApi = {
  banner: (): Promise<SupportBanner> => apiRequest("/api/support/banner/"),

  endByOwner: (): Promise<unknown> => apiRequest("/api/support/end/", { method: "POST" }),
};
